import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { baseUrl } from '../../comman/config';
import Loader from '../../components/Loader';
import { isFilterActive, matchFilterText, patchFilter } from "../../components/Filter/columnFilter";
import ColumnFilterRow from "../../components/Filter/ColumnFilterRow";
import { ItemName, itemDisplayName, itemSearchText, itemExportName } from "../../components/ItemName";
import { MenuViewFilter } from '../../components/Filter/DateSessionItemFilter';
import * as XLSX from 'xlsx';
import jsPDF from "jspdf";
import html2canvas from 'html2canvas';
import "jspdf-autotable";

const MenuView = () => {
    const [loading, setLoading] = useState(false);
    const [mealData, setMealData] = useState([]);
    // Whether a search has actually run against some criteria. An empty table means two different
    // things -- nothing asked for yet, or asked and nothing matched -- and the same sentence for
    // both told someone who had just searched to go and search.
    const [hasSearched, setHasSearched] = useState(false);
    const tableRef = useRef(null);
    const [error, setError] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [page, setPage] = useState(1);
    const [totalItem, setTotalItem] = useState(0);
    const [popupOpen, setPopupOpen] = useState(false);
    const [allEvents, setAllEvents] = useState([]);
    const [allSessions, setAllSessions] = useState([]);
    // Inline per-column filters: { [colKey]: { op, v1, v2 } }. This report is a pivot -- a row is
    // a date and each session column holds that day's items -- so a session filter matches against
    // the item names that cell lists, and a matching row keeps all of its columns.
    const [filters, setFilters] = useState({});
    const setColFilter = (key, patch) => setFilters((prev) => patchFilter(prev, key, patch));

    // Initialize with empty values rather than null/undefined
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [session, setSession] = useState([]);
    const [item, setItem] = useState([]);

    // Standard meal sessions in order of appearance
    const standardSessions = ['breakfast', 'lunch', 'dinner'];

    useEffect(() => {
        // Initial load without filters
        fetchAllEvents();
    }, []);

    // Function to format date as DD/MM/YYYY
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Function to fetch all events with their date ranges
    const fetchAllEvents = async () => {
        try {
            const response = await axios.get(`${baseUrl}/comman/list?table=event_master&condition=status=1`);
            if (response.data.status && response.data.data) {
                setAllEvents(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching events:', error);
        }
    };

    // Function to find all events that contain a specific date
    const findEventsForDate = (dateString) => {
        // Convert date string to date object for comparison
        const date = new Date(dateString);

        // Find all events where date falls between start_date and end_date
        const matchingEvents = allEvents.filter(event => {
            if (!event.start_date || !event.end_date) return false;

            const startDate = new Date(event.start_date);
            const endDate = new Date(event.end_date);

            return date >= startDate && date <= endDate;
        });

        return matchingEvents;
    };

    // Function to sort sessions with standard ones first
    const sortSessions = (sessions) => {
        // Start with standard sessions in the correct order
        const result = [...standardSessions].filter(session => sessions.includes(session));
        
        // Add any other sessions in alphabetical order
        const otherSessions = sessions.filter(session => !standardSessions.includes(session)).sort();
        
        return [...result, ...otherSessions];
    };

    const fetchMealData = async (fromDate, toDate, session, item, event) => {
        try {
            setLoading(true);
            setError(null);
            setPopupOpen(true);

            const sessionIds = session && session.length ? session.map(s => s.value).join(',') : '';
            const itemIds = item && item.length ? item.map(i => i.value).join(',') : '';
            const eventId = event && event.value !== 'all' ? event.value : '';

            if (eventId || fromDate || toDate) {
                setHasSearched(true);
                const response = await axios.get(
                    `${baseUrl}/menu_creation/details?event_id=${eventId}&from_date=${fromDate}&to_date=${toDate}&session_ids=${sessionIds}&item_ids=${itemIds}&page=${page}`
                );

                if (response.data.status && response.data.data) {
                    const processedData = response.data.data.map(dayData => {
                        // Find all events that this date belongs to
                        const dateEvents = findEventsForDate(dayData.date);

                        // If we have matching events, store them with the day data
                        if (dateEvents.length > 0) {
                            dayData.events = dateEvents;
                        }

                        // Ensure all standard sessions exist
                        if (dayData.meals) {
                            standardSessions.forEach(session => {
                                if (!dayData.meals[session]) {
                                    dayData.meals[session] = [];
                                }
                            });
                        }

                        return dayData;
                    });
                    
                    setMealData(processedData);
                    
                    // Extract all unique session keys from the meals objects
                    const uniqueSessions = new Set();
                    processedData.forEach(dayData => {
                        if (dayData.meals) {
                            Object.keys(dayData.meals).forEach(session => {
                                uniqueSessions.add(session);
                            });
                        }
                    });

                    // Ensure standard sessions are included even if they don't exist in data
                    standardSessions.forEach(session => uniqueSessions.add(session));
                    
                    // Set sessions in proper order
                    setAllSessions(sortSessions(Array.from(uniqueSessions)));
                    
                    setTotalItem(response.data.totalItem || 0);
                    if (eventId && eventId !== 'all') {
                        setSelectedEvent({
                            id: eventId,
                            name: event.label
                        });
                    } else {
                        setSelectedEvent(null);
                    }
                } else {
                    setMealData([]);
                    setAllSessions(standardSessions);
                    setTotalItem(0);
                }
            } else {
                setHasSearched(false);
                setMealData([]);
                setAllSessions(standardSessions);
                setTotalItem(0);
            }

            setLoading(false);
            setPopupOpen(false);
        } catch (error) {
            console.error('Error fetching meal data:', error);
            setError('Failed to load meal data');
            setLoading(false);
            setPopupOpen(false);
        }
    };

    // This matches the reference code's pattern more closely
    const handleFilterChange = (fromDate, toDate, session, item, event, eventImmediateUpdate = false) => {
        setFromDate(fromDate);
        setToDate(toDate);
        setSession(session || []);
        setItem(item || []);

        // If this is an event change or immediate update is requested, fetch data immediately
        if (eventImmediateUpdate) {
            fetchMealData(fromDate, toDate, session, item, event);
        }
    };
    
    // New function to handle submit button click
    const handleSubmitFilters = () => {
        // Create event object based on the selectedEvent
        const eventObj = selectedEvent ?
            { value: selectedEvent.id, label: selectedEvent.name } :
            { value: 'all', label: 'All' };

        fetchMealData(fromDate, toDate, session, item, eventObj);
    };

    // The PDF is a picture of the table rather than text drawn cell by cell.
    //
    // jsPDF writes one glyph per codepoint in logical order and does no OpenType shaping -- there
    // is no GSUB, GPOS or harfbuzz anywhere in its dist. Gujarati needs both: a virama has to fuse
    // its neighbours into a conjunct, and a pre-base matra has to be reordered to draw before the
    // consonant it follows in the text. So "ગ્રીન" came out as five loose glyphs with the virama
    // showing, and "મિક્સ" put its િ on the wrong side. The font was never the problem --
    // NotoSansGujarati carries the glyphs and the tables to combine them; jsPDF just never asks.
    //
    // The browser already shapes this correctly, which is why the screen looks right. Capturing
    // the rendered table and placing that image keeps what the reader can already see.
    //
    // The trade, worth knowing: the text in the PDF is no longer selectable or searchable. Getting
    // both would mean shaping in JS (harfbuzzjs) or printing through the browser.
    const handleExportPdf = async () => {
        if (!mealData || mealData.length === 0) {
            console.log('No data to export');
            return;
        }
        if (!tableRef.current) return;

        const eventName = selectedEvent ? selectedEvent.name : "Event";
        const eventTitle = `${eventName} General Kitchen Menu`;
        const eventSubTitle = `Shibir Dates : ${fromDate ? formatDate(fromDate) : ''} - ${toDate ? formatDate(toDate) : ''}`;

        try {
            setLoading(true);
            // scale 2 so the text stays sharp in print rather than pixelating at A4 width.
            const canvas = await html2canvas(tableRef.current, { scale: 2, backgroundColor: '#ffffff' });
            const image = canvas.toDataURL('image/png');

            // Landscape: a menu is wider than it is tall once every session has a column.
            const doc = new jsPDF({ orientation: 'landscape' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 10;
            const topOffset = 28;   // room for the two title lines

            // The two title lines are still drawn as text, since they are the event name and the
            // date range and they keep the PDF's heading selectable. Event names in use are
            // Latin, so the shaping problem does not reach them -- but it would if one were ever
            // named in Gujarati, and the fix then is to move the heading inside the captured
            // element rather than to reinstate the font.
            doc.setFontSize(14);
            doc.text(eventTitle, pageWidth / 2, 14, { align: "center" });
            doc.setFontSize(11);
            doc.text(eventSubTitle, pageWidth / 2, 21, { align: "center" });

            const usableWidth = pageWidth - margin * 2;
            const imageHeight = (canvas.height * usableWidth) / canvas.width;

            if (imageHeight <= pageHeight - topOffset - margin) {
                doc.addImage(image, 'PNG', margin, topOffset, usableWidth, imageHeight);
            } else {
                // Taller than one page: slice it down the pages rather than squashing it flat.
                let remaining = imageHeight;
                let position = topOffset;
                doc.addImage(image, 'PNG', margin, position, usableWidth, imageHeight);
                remaining -= (pageHeight - topOffset - margin);
                while (remaining > 0) {
                    position = margin - (imageHeight - remaining);
                    doc.addPage();
                    doc.addImage(image, 'PNG', margin, position, usableWidth, imageHeight);
                    remaining -= (pageHeight - margin * 2);
                }
            }

            doc.save(`${eventName}_MenuData_Report.pdf`);
        } catch (error) {
            console.error('Error exporting PDF:', error);
            setError('Failed to export PDF');
        } finally {
            setLoading(false);
        }
    };

    const handleExportExcel = () => {
        if (!mealData || mealData.length === 0) {
            console.log('No data to export');
            return;
        }

        const eventName = selectedEvent ? selectedEvent.name : "Event";
        const formattedTitle = `${eventName} General Kitchen Menu \nShibir Dates : ${fromDate ? formatDate(fromDate) : ''} - ${toDate ? formatDate(toDate) : ''}`;

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet([]);

        // Add title at the top
        XLSX.utils.sheet_add_aoa(worksheet, [[formattedTitle]], { origin: "A1" });

        // Create dynamic column headers with all sessions
        const headers = ["Date", ...allSessions.map(session => 
            session.charAt(0).toUpperCase() + session.slice(1))];

        // Add table headers
        XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A3" });

        // Create rows with all meal items in a single cell
        let rowIndex = 4;
        const bodyRows = [];   // kept so the column widths can be measured from real content

        mealData.forEach(dayData => {
            const dateObj = new Date(dayData.date);
            //const date = new Date(dayData.date).toLocaleDateString('en-GB');
            const formattedDate = `${dateObj.getDate()}-${dateObj.toLocaleString("en-GB", { month: "short" })}-${dateObj.getFullYear()}`;
            const dayName = new Date(dayData.date).toLocaleDateString("en-GB", { weekday: "short" });
            const fullDate = `${formattedDate}, ${dayName}`;
            // const event = 
            //     (dayData.events && dayData.events.length > 0 ?
            //         dayData.events.map(e => e.event_name).join(', ') :
            //         (selectedEvent ? selectedEvent.name : "-"));

            // Start with Event and Date
            //const rowData = [event, fullDate];
            const rowData = [fullDate];
            
            // Add each session's data
            allSessions.forEach(session => {
                const sessionItems = (dayData.meals[session] || []).map(itemExportName).join(', ');
                rowData.push(sessionItems || '-');
            });

            XLSX.utils.sheet_add_aoa(worksheet, [rowData], { origin: `A${rowIndex}` });
            bodyRows.push(rowData);
            rowIndex++;
        });

        // What follows is everything the xlsx community build actually writes. Bold, borders,
        // fills, wrap text, frozen panes and page setup are Pro features -- the free build accepts
        // a cell's `s` and drops it. This used to set alignment.wrapText on every session cell and
        // then give the rows 60pt to wrap into; neither reached the file, so the result was rows
        // four times taller than their one unwrapped line. Verified by unzipping the output: no
        // wrap in styles.xml, no <pane>, no <pageSetup>.

        // Widths from the longest thing in each column rather than a flat 40. A day with two items
        // was getting the same column as one with twelve -- a page of white space beside a
        // truncated cell. Capped so one long day cannot push the sheet off the page.
        const columnWidth = (index) => {
            const cells = [headers[index], ...bodyRows.map((row) => row[index])];
            const longest = cells.reduce((max, cell) => Math.max(max, String(cell ?? '').length), 0);
            return { wch: Math.min(Math.max(longest + 2, 12), 60) };
        };
        worksheet['!cols'] = headers.map((_, index) => columnWidth(index));

        // Merge title cells across all columns
        worksheet["!merges"] = [{ 
            s: { r: 0, c: 0 }, 
            e: { r: 0, c: headers.length - 1 } 
        }];

        // Title and header only -- data rows keep their natural height, since without wrap text a
        // tall row holds exactly the same single line as a short one.
        if (!worksheet['!rows']) worksheet['!rows'] = [];
        worksheet['!rows'][0] = { hpt: 30 }; // Title
        worksheet['!rows'][2] = { hpt: 20 }; // Header

        // Narrow margins: the sheet is wide and every cm counts across a menu's session columns.
        worksheet['!margins'] = { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 };

        // Download the file
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Menu Data');
        XLSX.writeFile(workbook, `${eventName}_MenuData_Report.xlsx`);
    };

    const renderMealItems = (items) => {
        if (!items || items.length === 0) return "-";

        return (
            <div className="flex flex-wrap gap-2">
                {items.map((item, index) => (
                    <span
                        key={`${item.id}-${index}`}
                        className="bg-gray-200 px-2 py-1 rounded-full text-sm"
                        title={item.eng_name} // Show English name on hover
                    >
                        <ItemName item={item} />
                    </span>
                ))}
            </div>
        );
    };

    // Render the events for a specific date
    const renderDateEvents = (dayData) => {
        // If no events property or empty events array
        if (!dayData.events || dayData.events.length === 0) {
            return dayData.event_name || '-';
        }
        
        // If there are events, render them
        return (
            <div className="flex flex-col gap-1">
                {dayData.events.map((event) => (
                    <span key={event.id} className="text-sm">
                        {event.event_name}
                    </span>
                ))}
            </div>
        );
    };

    const filterCols = [
        { key: 'event', text: (d) => (d.events?.length ? d.events.map((e) => e.event_name).join(', ') : (d.event_name ?? '')) },
        { key: 'date',  text: (d) => formatDate(d.date) },
        ...allSessions.map((session) => ({
            key: `session_${session}`,
            // Both names, so a session's items are found by whichever language was typed.
            text: (d) => (d.meals?.[session] ?? []).map(itemSearchText).join(', '),
        })),
    ];
    const activeFilterCols = filterCols.filter((c) => isFilterActive(filters[c.key]));
    const visibleDays = mealData.filter((d) => activeFilterCols.every((c) => matchFilterText(c.text(d), filters[c.key])));

    return (
        <div className="p-4">
            {loading && <Loader />}

            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-2xl font-bold">Menu Report</h1>
                <div>
                    <a
                        href="/item_category/sequence"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                            Edit Sequence
                        </button>
                    </a>
                    <Link to="/">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Back</button>
                    </Link>
                </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <MenuViewFilter
                    handleSubmitFilters={handleSubmitFilters}
                    handleFilterChange={handleFilterChange}
                    handleExport={handleExportExcel}
                    handleExportPdf={handleExportPdf}
                    handleClearFilters={() => setFilters({})}
                />
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                    {error}
                </div>
            )}

            <div className="relative shadow-md sm:rounded-lg">
                <div className="overflow-x-auto" ref={tableRef}>
                    {mealData.length > 0 ? (
                        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                <tr>
                                    <th scope="col" className="px-6 py-3 border border-black text-center">Event</th>
                                    <th scope="col" className="px-6 py-3 border border-black text-center">Date</th>
                                    {allSessions.map(session => (
                                        <th key={session} scope="col" className="px-6 py-3 border border-black text-center">
                                            {session.charAt(0).toUpperCase() + session.slice(1)}
                                        </th>
                                    ))}
                                </tr>
                                {/* Inline per-column filter row (contains / = / ≤ / ≥ / between / not between) */}
                                <ColumnFilterRow
                                    thClassName="px-1 py-1 border border-black bg-gray-50 align-top"
                                    cols={filterCols.map((c) => ({ key: c.key }))}
                                    filters={filters}
                                    onChange={setColFilter} />
                            </thead>
                            <tbody>
                                {visibleDays.map((dayData) => (
                                    <tr key={dayData.date} className="border-b hover:bg-gray-50 ">
                                        <td className="px-6 py-4 border border-black text-center font-medium">
                                            {renderDateEvents(dayData)}
                                        </td>
                                        <td className="px-6 py-4 border border-black text-center">
                                            {formatDate(dayData.date)}
                                        </td>
                                        {allSessions.map(session => (
                                            <td key={`${dayData.date}-${session}`} className="px-6 py-4 border border-black text-center">
                                                {renderMealItems(dayData.meals[session])}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="text-center p-8 bg-gray-50 rounded-md">
                            {hasSearched
                                ? "No Data Found"
                                : "Please select an event or date range to view menu data."}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MenuView;
